import { Injectable } from "@angular/core";
import { IProgress } from "../../interfaces/i-progress";
import { GeoService } from "./geo.service";
import { BehaviorSubject } from "rxjs";
import { ElementsService } from "./elements.service";
import { IPropertyResponse } from "../../interfaces/i-property-response";
import { PropertyFieldsResolver } from "./property-fields-resolver.service";

@Injectable({
  providedIn: "root",
})
export class ReportService {
  private propertyCount = 0;

  public progressBarPercent = 0;

  protected progressBarMessageSubject = new BehaviorSubject<string>(null);

  public progressBarMessage$ = this.progressBarMessageSubject.asObservable();

  protected progressBarValueSubject = new BehaviorSubject<IProgress>(null);

  public progressBarValue$ = this.progressBarValueSubject.asObservable();

  private maxRetries = 2;

  constructor(
    protected elementsService: ElementsService,
    private propertyFieldsResolver: PropertyFieldsResolver,
  ) {}
  // region Properties/Report

  async getDamageAssessment(aoi: any): Promise<void> {
    this.propertyCount = 0;
    this.progressBarPercent = 0;
    const propertiesDamage = new Array(aoi.features.length);

    let progress: IProgress = {
      current: 0,
      total: aoi.features.length,
    };
    this.setProgressBarValue(progress);
    this.setProgressBarMessage("Retrieving damage info...");

    const requests = aoi.features.map((feature: any, index: number) => {
      return async () => {
        const wkt = feature.wkt;

        const onFailure = () => {
          propertiesDamage[index] = {
            longitude: feature.lon,
            latitude: feature.lat,
            properties: feature.properties,
          };
        };

        const requestFn = async () => {
          const result = await this.requestGetPropertyDamage(wkt, aoi, feature);
          if (result.response) {
            const data: any = result.response;
            data.longitude = result.requestFeature.lon;
            data.latitude = result.requestFeature.lat;
            data.properties = result.requestFeature.properties;
            propertiesDamage[index] = data;
          }
        };

        await this.executeWithRetries(
          requestFn,
          this.maxRetries,
          progress,
          (count, prog) => {
            this.processDamageResponse(count, prog, propertiesDamage, aoi);
          },
          onFailure,
        );
      };
    });

    const maxConcurrentRequests = 20;
    await this.runConcurrently(requests, maxConcurrentRequests);
  }

  async getPropertyInformation(productSelected: any, features: any[], layerName: string): Promise<void> {
    this.propertyCount = 0;
    this.progressBarPercent = 0;
    const properties = new Array(features.length); // Inicializar el array con la longitud correcta

    let progress: IProgress = {
      current: 0,
      total: features.length,
    };
    this.setProgressBarValue(progress);
    this.setProgressBarMessage("Retrieving property information...");

    const requests = features.map((feature, index) => {
      return async () => {
        const point = GeoService.pointToGeoJSONWGS84(feature.geometry.coordinates);
        feature.lon = point.geometry.coordinates[0];
        feature.lat = point.geometry.coordinates[1];
        const wkt = GeoService.pointToWkt(point.geometry.coordinates);

        const onFailure = () => {
          properties[index] = {
            longitude: feature.lon,
            latitude: feature.lat,
            properties: feature.properties,
          };
        };

        const requestFn = () =>
          this.requestGetPropertyInfo(productSelected, wkt, properties, progress, feature, layerName, index);

        await this.executeWithRetries(
          requestFn,
          this.maxRetries,
          progress,
          (count, prog) => {
            this.processPropertyResponse(count, progress, properties, productSelected, layerName);
          },
          onFailure,
        );
      };
    });

    const maxConcurrentRequests = 50;
    await this.runConcurrently(requests, maxConcurrentRequests);
  }

  async executeWithRetries(
    requestFn: () => Promise<void>,
    maxRetries: number,
    progress: IProgress,
    onComplete: (count: number, progress: IProgress) => void,
    onFailure: () => void,
  ): Promise<void> {
    let attempt = 0;
    while (attempt < maxRetries) {
      try {
        await requestFn();
        break;
      } catch (error) {
        attempt++;
        if (attempt >= maxRetries) {
          console.error("Max retries reached, request failed:", error);
          onFailure();
        }
      }
    }

    onComplete(progress.current, progress);
  }

  async runConcurrently(tasks: (() => Promise<void>)[], concurrentLimit: number): Promise<void> {
    const executing = [];
    for (const task of tasks) {
      const promise = task().then(() => executing.splice(executing.indexOf(promise), 1));
      executing.push(promise);

      if (executing.length >= concurrentLimit) {
        await Promise.race(executing);
      }
    }
    await Promise.all(executing);
  }

  async requestGetPropertyDamage(wkt: string, aoi: any, feature: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.elementsService.requestDamageInformation(wkt, aoi.parentName).subscribe({
        next: (response: any) => {
          resolve({ requestFeature: feature, response });
        },
        error: (err) => {
          reject(err);
        },
      });
    });
  }

  processDamageResponse(count: number, progress: IProgress, properties: any[], aoi: any) {
    this.updateProgressBar(progress);
    if (this.propertyCount === progress.total) {
      const csv = this.convertToCSV(this.buildDamageResolverJson(properties));
      const aoiFileName = aoi.prettyName ? aoi.prettyName : aoi.name;
      const fileName = aoiFileName + " damage assessment.csv";
      this.downloadCsvFile(csv, fileName);
      this.propertyCount = 0;
    }
  }

  processPropertyResponse(
    count: number,
    progress: IProgress,
    properties: any[],
    productSelected: any,
    layerName: string,
  ) {
    this.updateProgressBar(progress);
    if (this.propertyCount === progress.total) {
      const csv = this.convertToCSV(this.buildPropertyResolverJson(properties, productSelected));
      const fileName = `${layerName} ${productSelected.NAME}.csv`;
      this.downloadCsvFile(csv, fileName);
      this.propertyCount = 0;
    }
  }

  async requestGetPropertyInfo(
    type: any,
    wkt: string,
    properties: any[],
    progress: IProgress,
    feature: any,
    layer: string,
    featureIndex: number,
  ): Promise<void> {
    const onDemand = false;

    const response: IPropertyResponse = await this.elementsService
      .requestPropertyInformation(wkt, onDemand, type.PARAMETER)
      .toPromise();

    response.longitude = feature.lon;
    response.latitude = feature.lat;
    response.properties = feature.properties;
    properties[featureIndex] = response;
  }

  updateProgressBar(progress) {
    this.propertyCount += 1;
    progress.current = this.propertyCount;
    const currentProgress = Math.round((progress.current / progress.total) * 20) * 5;

    if (this.progressBarPercent !== currentProgress) {
      this.progressBarPercent = currentProgress;
      this.setProgressBarValue(progress);
    }
  }

  convertToCSV(json) {
    const jsonCopy = structuredClone(json);

    const keysSet = new Set<string>();
    jsonCopy.forEach((item) => {
      Object.keys(item).forEach((key) => keysSet.add(key));
    });
    const keys = Array.from(keysSet) as string[];

    const newArray = jsonCopy.map((el) => {
      let property: { [key: string]: any } = {};
      keys.forEach((key) => {
        property[key] = el[key] !== undefined ? el[key] : null;
      });
      return property;
    });

    const csvRows = [
      keys.join(","),
      ...newArray.map((it) => {
        return keys
          .map((key) => {
            let value = it[key] !== null && it[key] !== undefined ? it[key].toString() : "";
            value = value.replace(/^"+|"+$/g, "");
            return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
          })
          .join(",");
      }),
    ];

    return csvRows.join("\n");
  }

  downloadCsvFile(csvData: string, fileName: string): void {
    this.setProgressBarMessage(null);
    this.setProgressBarValue(null);
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("href", url);
    a.setAttribute("download", fileName);
    a.click();
  }

  setProgressBarMessage(message: string) {
    this.progressBarMessageSubject.next(message);
  }

  getProgressBarMessage(): string {
    return this.progressBarMessageSubject.getValue();
  }

  setProgressBarValue(progress: IProgress) {
    this.progressBarValueSubject.next(progress);
  }

  getProgressBarValue(): IProgress {
    return this.progressBarValueSubject.getValue();
  }

  /**
   * Builds a flat export record for each property using PropertyFieldsResolver.
   * Mirrors preparePropertyJson for structure-attributes / structure-geometry / parcel-attributes.
   * Used only for resolver output verification — does not replace the legacy CSV generation.
   */
  private buildPropertyResolverJson(properties: any[], _productSelected: any): Record<string, any>[] {
    // Keys whose values are nested objects handled separately below
    const NESTED_KEYS = new Set([
      "structures",
      "property_features",
      "properties",
      "post_catastrophe",
      "pre_catastrophe",
      "latitude",
      "longitude",
    ]);

    return properties.map((property) => {
      const data: Record<string, any> = {
        lat: property.latitude,
        lon: property.longitude,
      };

      // GIS feature properties (address, parcel id, etc.)
      Object.keys(property.properties ?? {}).forEach((key) => {
        data[key] = property.properties[key];
      });

      // Top-level scalar fields: ortho_date_estimate, inside_parcel, requested_wkt, etc.
      const topLevel: Record<string, any> = {};
      Object.keys(property).forEach((key) => {
        if (!NESTED_KEYS.has(key)) topLevel[key] = property[key];
      });
      Object.assign(data, this.propertyFieldsResolver.toExportRecord(topLevel, ""));

      if (property.property_features) {
        Object.assign(data, this.propertyFieldsResolver.toExportRecord(property.property_features, ""));
      }

      if (property.structures) {
        const primary = property.structures.find((s: any) => s.primaryStructure);
        if (primary) {
          Object.assign(data, this.propertyFieldsResolver.toExportRecord(primary, "Primary Structure"));
          // footPrint.wkt is excluded from toExportRecord (hidden from UI) — add explicitly to CSV
          if (primary.footPrint?.wkt) {
            data["Primary Structure Footprint WKT"] = ReportService.normalizeFootprintWkt(primary.footPrint.wkt);
          }
        }
        let structureCount = 1;
        property.structures.forEach((structure: any) => {
          if (!structure.primaryStructure) {
            const prefix = `Additional Structure #${structureCount}`;
            Object.assign(data, this.propertyFieldsResolver.toExportRecord(structure, prefix));
            // footPrint.wkt is excluded from toExportRecord (hidden from UI) — add explicitly to CSV
            if (structure.footPrint?.wkt) {
              data[`${prefix} Footprint WKT`] = ReportService.normalizeFootprintWkt(structure.footPrint.wkt);
            }
            structureCount += 1;
          }
        });
      }

      return data;
    });
  }

  /**
   * Builds a flat export record for each damage-assessment property using PropertyFieldsResolver.
   * Mirrors prepareDamageJson.
   * Used only for resolver output verification — does not replace the legacy CSV generation.
   */
  private buildDamageResolverJson(properties: any[]): Record<string, any>[] {
    return properties.map((property) => {
      const data: Record<string, any> = {
        lat: property.latitude,
        lon: property.longitude,
      };

      Object.keys(property.properties ?? {}).forEach((key) => {
        data[key] = property.properties[key];
      });

      if (property.post_catastrophe) {
        const primary = property.post_catastrophe.structure.find((s: any) => s.primaryStructure);
        if (primary) {
          Object.assign(data, this.propertyFieldsResolver.toExportRecord(primary, "Primary Building"));
          // footPrint.wkt is excluded from toExportRecord (hidden from UI) — add explicitly to CSV
          if (primary.footPrint?.wkt) {
            data["Primary Building Footprint WKT"] = ReportService.normalizeFootprintWkt(primary.footPrint.wkt);
          }
        }
        let structureCount = 1;
        property.post_catastrophe.structure.forEach((structure: any) => {
          if (!structure.primaryStructure) {
            const prefix = `Additional Building #${structureCount}`;
            Object.assign(data, this.propertyFieldsResolver.toExportRecord(structure, prefix));
            // footPrint.wkt is excluded from toExportRecord (hidden from UI) — add explicitly to CSV
            if (structure.footPrint?.wkt) {
              data[`${prefix} Footprint WKT`] = ReportService.normalizeFootprintWkt(structure.footPrint.wkt);
            }
            structureCount += 1;
          }
        });
      }

      if (property.pre_catastrophe?.property_features) {
        Object.assign(data, this.propertyFieldsResolver.toExportRecord(property.pre_catastrophe.property_features, ""));
      }

      return data;
    });
  }

  /**
   * Normalizes a footprint WKT to POLYGON type.
   * Building footprints are simple polygons; when the API returns them as MULTIPOLYGON,
   * the first polygon is extracted and returned as POLYGON.
   * @param wkt - Footprint WKT string from the API
   */
  private static normalizeFootprintWkt(wkt: string): string {
    if (!wkt) return wkt;
    if (!/^MULTIPOLYGON\s*\(/i.test(wkt.trimStart())) return wkt;
    // MULTIPOLYGON (((ring))) → POLYGON ((ring))
    // Captures the first polygon group: the content wrapped by the outer double-parens
    const match = /^MULTIPOLYGON\s*\(\s*(\(\s*\([^)]+\)\s*\))/i.exec(wkt.trimStart());
    return match ? `POLYGON ${match[1]}` : wkt;
  }

  // endregion
}
